import { zodResolver } from "@hookform/resolvers/zod";
import { NcForm, NcFormTextField, NcPage } from "@noted/noted-components";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { usersQueries } from "~/api/users";
import { useAccount } from "~/components/account-context/account.context";
import { useToast } from "~/components/alerts/toast-provider";
import { useI18n } from "~/hooks/use-i18n";
import { IAccount } from "~/types";

const baseFormSchema = (t: (arg: string) => string) =>
  z.object({
    email: z.string().email(t("admin-users:email_valid")),
    title: z.string(),
    firstName: z.string().min(1, t("admin-users:first_name_required")),
    lastName: z.string().min(1, t("admin-users:last_name_required")),
    registrationNumber: z.string(),
    shortCode: z.string().max(2, t("admin-users:short_code_max_length")).optional(),
    phone: z.string(),
  });

export const UserDetails = ({ user }: { user: IAccount }) => {
  const { t } = useI18n(["admin-users"]);
  const { organisation, invalidate } = useAccount();
  const { mutate: updateUser, isPending: isSaving } = useMutation({
    ...usersQueries.update,
    onSuccess: async () => {
      enqueueSuccess(t("admin-users:save_success"));
      await invalidate();
    },
    onError: () => {
      enqueueError(t("admin-users:save_error"));
    },
  });
  const { enqueueSuccess, enqueueError } = useToast();

  const formSchema = baseFormSchema(t);

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      title: user?.title ?? "",
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
      registrationNumber: user?.registrationNumber ?? "",
      shortCode: user?.shortCode ?? "",
      phone: user?.phone ?? "",
      email: user?.email ?? "",
    },
    resolver: zodResolver(formSchema),
  });

  const { handleSubmit } = form;

  const onFormSubmit = handleSubmit(input => {
    if (isSaving || organisation.readOnlyMode) {
      return;
    }

    updateUser({
      id: user.id,
      title: input.title,
      firstName: input.firstName,
      lastName: input.lastName,
      email: input.email,
      phone: input.phone,
      registrationNumber: input.registrationNumber,
      shortCode: input.shortCode,
      // TODO: why do I need to pass the organisation ID?  Can we default to auth'ed users org?
      organisationId: organisation.id,
      securityRoles: user.securityRoles,
      // For some reason the users privileges includes both "user privileges" and "role privileges".
      // I have no idea why this has been done, but we don't want to send "role privileges" in the
      // update user API call.
      privileges: user.privileges?.filter(p => !p.roleIds).map(({ key }) => ({ key })) ?? [],
    });
  });

  return (
    <NcPage>
      <NcPage.Wrapper>
        <NcPage.Header>
          <NcPage.Heading>{t("admin-users:user_details")}</NcPage.Heading>
        </NcPage.Header>
        <NcPage.Body>
          <NcForm onSubmit={onFormSubmit} form={form}>
            <NcFormTextField
              name="firstName"
              label={t("admin-users:first_name")}
              isDisabled={organisation.readOnlyMode}
              isRequired
              inputWidth="large"
            />
            <NcFormTextField
              name="lastName"
              label={t("admin-users:last_name")}
              isDisabled={organisation.readOnlyMode}
              isRequired
              inputWidth="large"
            />
            <NcFormTextField
              name="email"
              type="email"
              label={t("admin-users:contact_email_label")}
              description={t("admin-users:contact_email_description")}
              isDisabled={organisation.readOnlyMode}
              inputWidth="large"
              isRequired
            />
            <NcFormTextField
              name="title"
              label={t("admin-users:title")}
              isDisabled={organisation.readOnlyMode}
              inputWidth="small"
            />
            <NcFormTextField
              name="phone"
              label={t("admin-users:phone")}
              isDisabled={organisation.readOnlyMode}
            />
            <NcFormTextField
              name="registrationNumber"
              label={t("admin-users:registration_number")}
              isDisabled={organisation.readOnlyMode}
            />
            <NcFormTextField
              name="shortCode"
              label={t("admin-users:short_code")}
              isDisabled={organisation.readOnlyMode}
              inputWidth="xsmall"
            />

            <NcForm.Actions>
              <NcForm.SubmitButton isDisabled={organisation.readOnlyMode} isLoading={isSaving}>
                {t("admin-users:submit")}
              </NcForm.SubmitButton>
            </NcForm.Actions>
          </NcForm>
        </NcPage.Body>
      </NcPage.Wrapper>
    </NcPage>
  );
};
